import { QueryClient } from '@tanstack/react-query'

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 2,      // 2 minutes
      gcTime:    1000 * 60 * 10,     // 10 minutes
      retry: (failureCount, error: unknown) => {
        // Don't retry on 4xx errors
        if (error && typeof error === 'object' && 'code' in error) {
          const code = (error as { code: string }).code
          if (['PGRST116', 'PGRST301', '42501'].includes(code)) return false
        }
        return failureCount < 2
      },
      refetchOnWindowFocus: true,
    },
    mutations: {
      retry: false,
    },
  },
})

export const queryKeys = {
  restaurants:    () => ['restaurants'] as const,
  restaurant:     (id: string) => ['restaurants', id] as const,
  users:          (restaurantId: string) => ['users', restaurantId] as const,
  menuCategories: (restaurantId: string) => ['menu-categories', restaurantId] as const,
  menuItems:      (restaurantId: string) => ['menu-items', restaurantId] as const,
  menuItem:       (id: string) => ['menu-items', 'detail', id] as const,
  dailyMenu:      (restaurantId: string, date: string) => ['daily-menu', restaurantId, date] as const,
  inventory:              (restaurantId: string) => ['inventory', restaurantId] as const,
  inventoryPerishable:    (restaurantId: string) => ['inventory-perishable', restaurantId] as const,
  inventoryNonperishable: (restaurantId: string) => ['inventory-nonperishable', restaurantId] as const,
  suppliers:              (restaurantId: string) => ['suppliers', restaurantId] as const,
  purchaseOrders:         (restaurantId: string) => ['purchase-orders', restaurantId] as const,
  purchaseOrder:          (id: string) => ['purchase-orders', 'detail', id] as const,
  productionPlan: (restaurantId: string, date: string) => ['production-plan', restaurantId, date] as const,
  salesRecords:   (restaurantId: string) => ['sales-records', restaurantId] as const,
  aiAlerts:       (restaurantId: string) => ['ai-alerts', restaurantId] as const,
  catering:           (restaurantId: string) => ['catering', restaurantId] as const,
  cateringOrder:      (id: string) => ['catering', 'detail', id] as const,
  cateringRevisions:  (cateringId: string) => ['catering', 'revisions', cateringId] as const,
  cateringPricing:    (restaurantId: string) => ['catering-pricing', restaurantId] as const,
  storeOrders:        (restaurantId: string) => ['store-orders', restaurantId] as const,
  addons:             (restaurantId: string) => ['addons', restaurantId] as const,
  menuItemAddons:     (menuItemId: string)   => ['menu-item-addons', menuItemId] as const,
  ingredients:        (restaurantId: string) => ['ingredients', restaurantId] as const,
  ingredient:         (id: string) => ['ingredients', 'detail', id] as const,
  ingredientSuppliers:(ingredientId: string) => ['ingredient-suppliers', ingredientId] as const,
  recipe:             (menuItemId: string) => ['recipe', menuItemId] as const,
  procedure:          (menuItemId: string) => ['procedure', menuItemId] as const,
  buyers:             (restaurantId: string) => ['buyers', restaurantId] as const,
  buyerContacts:      (buyerId: string) => ['buyer-contacts', buyerId] as const,
  buyerProducts:      (buyerId: string) => ['buyer-products', buyerId] as const,
  invoices:           (restaurantId: string) => ['invoices', restaurantId] as const,
  invoice:            (id: string) => ['invoices', 'detail', id] as const,
  storeSupply:        (restaurantId: string) => ['store-supply', restaurantId] as const,
  productStock:       (restaurantId: string) => ['product-stock', restaurantId] as const,
  productStockTxns:   (menuItemId: string)   => ['product-stock-txns', menuItemId] as const,
  supplierCatalog:    (supplierId: string)   => ['supplier-catalog', supplierId] as const,
  supplierCatalogAll: (restaurantId: string) => ['supplier-catalog-all', restaurantId] as const,
}
